import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const STORAGE_KEY = 'veloxThemePreference';
const THEME_API_PATH = '/api/user/theme-preference';

/**
 * Centralized theme management hook
 * Handles theme state, persistence, and API sync
 * 
 * @returns {Object} { isDarkMode, toggleTheme, setIsDarkMode }
 */
export const useTheme = () => {
  // Initialize synchronously to prevent flash of wrong theme
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);

    // Treat anything other than explicit 'light' as dark.
    // This makes dark the effective default for first-load and unauthenticated users.
    const shouldBeDark = saved !== 'light';

    // Set theme on HTML element immediately
    document.documentElement.setAttribute('data-theme', shouldBeDark ? 'dark' : 'light');
    return shouldBeDark;
  });

  // Load theme preference from API on mount (synced across devices)
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await api.get(THEME_API_PATH);
        
        if (response.data && response.data.themePreference) {
          // Backend already normalizes legacy/invalid values:
          // - 'dark' | 'light' only; 'system'/invalid are coerced to 'dark'
          const apiIsDark = response.data.themePreference === 'dark';
          if (apiIsDark !== isDarkMode) {
            setIsDarkMode(apiIsDark);
            document.documentElement.setAttribute('data-theme', apiIsDark ? 'dark' : 'light');
            localStorage.setItem(STORAGE_KEY, apiIsDark ? 'dark' : 'light');
          }
        }
      } catch (error) {
        // Silently fail - use localStorage preference
        console.log('Could not load theme from API, using local storage:', error);
      }
    };
    
    loadTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Apply theme changes and persist to localStorage and API
  useEffect(() => {
    const themeValue = isDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', themeValue);
    localStorage.setItem(STORAGE_KEY, themeValue);
    
    // Save to API for syncing across devices
    const saveThemePreference = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await api.post(THEME_API_PATH, { themePreference: themeValue });
        }
      } catch (error) {
        // Don't block UI if API save fails - localStorage is still saved
        console.error('Failed to save theme preference to API:', error);
      }
    };
    
    saveThemePreference();
  }, [isDarkMode]);

  // Toggle theme function
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => !prev);
  }, []);

  return { 
    isDarkMode, 
    toggleTheme, 
    setIsDarkMode 
  };
};

export default useTheme;

